/**
 * Queries / mutations React Query de la FAQ (admin, apps/app).
 *
 * `faq_items` (migration 0173) est postérieure aux types générés → cast léger
 * `faqDb` (même pattern que `diffDb`/`ytDb`). Écriture directe via PostgREST +
 * RLS admin (`faq_items: admin_all`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/** Accès à `faq_items` sans inférence PostgREST (table hors types générés). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const faqDb = supabase as unknown as { from: (table: string) => any };

export interface FaqItemRow {
  id: string;
  question: string;
  answer_md: string;
  display_order: number;
  is_published: boolean;
  /** Clé d'aide contextuelle (pastille « ? » de l'app, migration 0300). */
  context_key: string | null;
  /** Affichée aussi dans la FAQ du site (migration 0300). */
  show_on_landing: boolean;
  created_at: string;
  updated_at: string;
}

export interface FaqItemInput {
  question: string;
  answer_md: string;
  display_order?: number;
  is_published?: boolean;
  context_key?: string | null;
  show_on_landing?: boolean;
}

/** Erreur SQL → message lisible (clé d'aide déjà prise, format refusé). */
export function translateFaqError(message: string): string {
  if (message.includes('faq_items_context_key_uniq')) {
    return 'Cette clé d’aide est déjà utilisée par une autre question.';
  }
  if (message.includes('faq_items_context_key_chk')) {
    return 'Clé d’aide invalide : minuscules, chiffres et tirets seulement.';
  }
  return message;
}

const FAQ_KEY = ['admin-faq-items'] as const;

export function useAdminFaqItems() {
  return useQuery({
    queryKey: FAQ_KEY,
    queryFn: async (): Promise<FaqItemRow[]> => {
      const { data, error } = await faqDb
        .from('faq_items')
        .select(
          'id, question, answer_md, display_order, is_published, context_key, show_on_landing, created_at, updated_at',
        )
        .order('display_order', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as FaqItemRow[];
    },
  });
}

export function useCreateFaqItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: FaqItemInput) => {
      const { error } = await faqDb.from('faq_items').insert({
        question: input.question.trim(),
        answer_md: input.answer_md.trim(),
        display_order: input.display_order ?? 0,
        is_published: input.is_published ?? true,
        context_key: input.context_key ?? null,
        show_on_landing: input.show_on_landing ?? true,
      });
      if (error) throw new Error(translateFaqError(error.message));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FAQ_KEY });
      qc.invalidateQueries({ queryKey: ['help-topics'] });
    },
  });
}

export interface FaqItemUpdate extends Partial<FaqItemInput> {
  id: string;
}

export function useUpdateFaqItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: FaqItemUpdate) => {
      const { error } = await faqDb.from('faq_items').update(patch).eq('id', id);
      if (error) throw new Error(translateFaqError(error.message));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FAQ_KEY });
      qc.invalidateQueries({ queryKey: ['help-topics'] });
    },
  });
}

export function useDeleteFaqItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await faqDb.from('faq_items').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FAQ_KEY });
      qc.invalidateQueries({ queryKey: ['help-topics'] });
    },
  });
}

/** Échange l'ordre de deux items (réordonnancement haut/bas). */
export function useSwapFaqOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pair: {
      a: { id: string; order: number };
      b: { id: string; order: number };
    }) => {
      const r1 = await faqDb
        .from('faq_items')
        .update({ display_order: pair.b.order })
        .eq('id', pair.a.id);
      if (r1.error) throw new Error(r1.error.message);
      const r2 = await faqDb
        .from('faq_items')
        .update({ display_order: pair.a.order })
        .eq('id', pair.b.id);
      if (r2.error) throw new Error(r2.error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: FAQ_KEY });
      qc.invalidateQueries({ queryKey: ['help-topics'] });
    },
  });
}
